interface Model3DApi {
  id: string;
  name: string;
  thumbnail: string;
  downloadUrl: string;
  previewUrl: string;
  category: string;
  tags: string[];
  format: string;
  size: string;
  realistic: boolean;
  source: string;
  license: string;
  description: string;
  author?: string;
  rating?: number;
  downloads?: number;
}

interface SearchParams {
  query?: string;
  category?: string;
  source?: string;
  realistic?: boolean;
  format?: string;
  license?: string;
  tags?: string[];
  categories?: string[];
  page?: number;
  limit?: number;
}

interface SearchResult {
  models: Model3DApi[];
  total: number;
}

class ModelApiService {
  private static instance: ModelApiService;
  private cache: Map<string, SearchResult> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): ModelApiService {
    if (!ModelApiService.instance) {
      ModelApiService.instance = new ModelApiService();
    }
    return ModelApiService.instance;
  }

  // Modèles Khronos Group (officiels)
  private async fetchKhronosModels(): Promise<Model3DApi[]> {
    const khronosModels: Model3DApi[] = [
      {
        id: "khronos-rigged-figure",
        name: "RiggedFigure",
        thumbnail: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/RiggedFigure/screenshot/screenshot.png",
        downloadUrl: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/RiggedFigure/glTF-Binary/RiggedFigure.glb",
        previewUrl: "/model-viewer.html?model=RiggedFigure.glb",
        category: "human",
        tags: ["rigged", "animation", "test", "official"],
        format: "GLB",
        size: "1.2 MB",
        realistic: false,
        source: "Khronos Group",
        license: "CC0",
        description: "Modèle de test officiel avec squelette pour animations",
        author: "Khronos Group",
        rating: 5,
        downloads: 10000
      },
      {
        id: "khronos-simple-morph",
        name: "SimpleMorph",
        thumbnail: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/SimpleMorph/screenshot/screenshot.png",
        downloadUrl: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/SimpleMorph/glTF-Binary/SimpleMorph.glb",
        previewUrl: "/model-viewer.html?model=SimpleMorph.glb",
        category: "human",
        tags: ["morph", "animation", "simple", "facial"],
        format: "GLB",
        size: "0.8 MB",
        realistic: false,
        source: "Khronos Group",
        license: "CC0",
        description: "Modèle simple avec morphing facial",
        author: "Khronos Group",
        rating: 4,
        downloads: 5000
      },
      {
        id: "khronos-animated-morph-cube",
        name: "AnimatedMorphCube",
        thumbnail: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/AnimatedMorphCube/screenshot/screenshot.png",
        downloadUrl: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/AnimatedMorphCube/glTF-Binary/AnimatedMorphCube.glb",
        previewUrl: "/model-viewer.html?model=AnimatedMorphCube.glb",
        category: "test",
        tags: ["animation", "morph", "cube", "test"],
        format: "GLB",
        size: "0.5 MB",
        realistic: false,
        source: "Khronos Group",
        license: "CC0",
        description: "Cube animé avec morphing pour tests",
        author: "Khronos Group",
        rating: 3,
        downloads: 3000
      }
    ];

    return khronosModels;
  }

  // Modèles Sketchfab simulés (en réalité, il faudrait utiliser leur API)
  private async fetchSketchfabModels(): Promise<Model3DApi[]> {
    const sketchfabModels: Model3DApi[] = [
      {
        id: "sketchfab-realistic-male",
        name: "Realistic Human Male",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRlZGY3Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkhvbW1lIFLDqWFsaXN0ZTwvdGV4dD48L3N2Zz4=",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/RiggedFigure/glTF-Binary/RiggedFigure.glb",
        previewUrl: "/model-viewer.html?model=RiggedFigure.glb",
        category: "human",
        tags: ["realistic", "male", "photorealistic", "anatomy"],
        format: "GLB",
        size: "15.2 MB",
        realistic: true,
        source: "Sketchfab",
        license: "CC BY",
        description: "Modèle masculin photoréaliste haute qualité",
        author: "3D Artist",
        rating: 4.8,
        downloads: 2500
      },
      {
        id: "sketchfab-female-anatomy",
        name: "Female Anatomy Model",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmOGZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkZlbW1lIEFuYXRvbWllPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/SimpleMorph/glTF-Binary/SimpleMorph.glb",
        previewUrl: "/model-viewer.html?model=SimpleMorph.glb",
        category: "human",
        tags: ["anatomy", "female", "medical", "education"],
        format: "GLB",
        size: "12.8 MB",
        realistic: true,
        source: "Sketchfab",
        license: "CC BY-SA",
        description: "Modèle anatomique féminin détaillé pour l'éducation",
        author: "Medical 3D",
        rating: 4.6,
        downloads: 1800
      },
      {
        id: "sketchfab-sports-car",
        name: "Sports Car Lamborghini",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmZlYmVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxhbWJvcmdoaW5pPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/DamagedHelmet/glTF-Binary/DamagedHelmet.glb",
        previewUrl: "/model-viewer.html?model=DamagedHelmet.glb",
        category: "vehicle",
        tags: ["car", "sports", "luxury", "automotive"],
        format: "GLB",
        size: "25.4 MB",
        realistic: true,
        source: "Sketchfab",
        license: "CC BY",
        description: "Voiture de sport Lamborghini haute définition",
        author: "Car3D Studio",
        rating: 4.9,
        downloads: 8500
      },
      {
        id: "sketchfab-medieval-castle",
        name: "Medieval Castle",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNow6J0ZWF1IE1lZGnDqXZhbDwvdGV4dD48L3N2Zz4=",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/Sponza/glTF/Sponza.gltf",
        previewUrl: "/model-viewer.html?model=Sponza.gltf",
        category: "architecture",
        tags: ["castle", "medieval", "fortress", "historical"],
        format: "GLB",
        size: "18.7 MB",
        realistic: true,
        source: "Sketchfab",
        license: "CC BY-SA",
        description: "Château médiéval détaillé avec tours et remparts",
        author: "HistoryModels",
        rating: 4.7,
        downloads: 3200
      },
      {
        id: "sketchfab-dragon",
        name: "Fantasy Dragon",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmZlZWVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRyYWdvbiBGYW50YXN5PC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/Fox/glTF-Binary/Fox.glb",
        previewUrl: "/model-viewer.html?model=Fox.glb",
        category: "creature",
        tags: ["dragon", "fantasy", "mythical", "creature"],
        format: "GLB",
        size: "22.1 MB",
        realistic: false,
        source: "Sketchfab",
        license: "CC BY",
        description: "Dragon fantastique avec animations de vol",
        author: "FantasyArt3D",
        rating: 4.8,
        downloads: 6700
      },
      {
        id: "sketchfab-modern-house",
        name: "Modern House",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmOGZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk1haXNvbiBNb2Rlcm5lPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/BarramundiFish/glTF-Binary/BarramundiFish.glb",
        previewUrl: "/model-viewer.html?model=BarramundiFish.glb",
        category: "architecture",
        tags: ["house", "modern", "residential", "contemporary"],
        format: "GLB",
        size: "14.3 MB",
        realistic: true,
        source: "Sketchfab",
        license: "CC BY",
        description: "Maison moderne avec design contemporain",
        author: "ArchViz Pro",
        rating: 4.5,
        downloads: 4100
      }
    ];

    return sketchfabModels;
  }

  // Modèles Mixamo simulés
  private async fetchMixamoModels(): Promise<Model3DApi[]> {
    const mixamoModels: Model3DApi[] = [
      {
        id: "mixamo-character-1",
        name: "Mixamo Character - Business",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk1peGFtbyBCdXNpbmVzczwvdGV4dD48L3N2Zz4=",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/RiggedSimple/glTF-Binary/RiggedSimple.glb",
        previewUrl: "/model-viewer.html?model=RiggedSimple.glb",
        category: "human",
        tags: ["animated", "rigged", "business", "professional"],
        format: "FBX",
        size: "8.5 MB",
        realistic: true,
        source: "Mixamo",
        license: "Adobe License",
        description: "Personnage professionnel avec animations",
        author: "Adobe Mixamo",
        rating: 4.5,
        downloads: 5500
      },
      {
        id: "mixamo-character-2",
        name: "Mixamo Character - Casual",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWZmNGY3Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk1peGFtbyBDYXN1YWw8L3RleHQ+PC9zdmc+",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/CesiumMan/glTF-Binary/CesiumMan.glb",
        previewUrl: "/model-viewer.html?model=CesiumMan.glb",
        category: "human",
        tags: ["animated", "rigged", "casual", "everyday"],
        format: "FBX",
        size: "7.2 MB",
        realistic: true,
        source: "Mixamo",
        license: "Adobe License",
        description: "Personnage décontracté avec animations",
        author: "Adobe Mixamo",
        rating: 4.3,
        downloads: 4200
      },
      {
        id: "mixamo-warrior",
        name: "Fantasy Warrior",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmZlZWVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkd1ZXJyaWVyIEZhbnRhc3k8L3RleHQ+PC9zdmc+",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/BrainStem/glTF-Binary/BrainStem.glb",
        previewUrl: "/model-viewer.html?model=BrainStem.glb",
        category: "human",
        tags: ["warrior", "fantasy", "armor", "combat"],
        format: "FBX",
        size: "12.3 MB",
        realistic: false,
        source: "Mixamo",
        license: "Adobe License",
        description: "Guerrier fantastique avec armure et animations de combat",
        author: "Adobe Mixamo",
        rating: 4.7,
        downloads: 7800
      },
      {
        id: "mixamo-dancer",
        name: "Professional Dancer",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmOGZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRhbnNldXNlPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/AnimatedMorphCube/glTF-Binary/AnimatedMorphCube.glb",
        previewUrl: "/model-viewer.html?model=AnimatedMorphCube.glb",
        category: "human",
        tags: ["dance", "performance", "artistic", "movement"],
        format: "FBX",
        size: "9.1 MB",
        realistic: true,
        source: "Mixamo",
        license: "Adobe License",
        description: "Danseuse professionnelle avec animations de danse",
        author: "Adobe Mixamo",
        rating: 4.6,
        downloads: 3900
      }
    ];

    return mixamoModels;
  }

  // Modèles TurboSquid simulés
  private async fetchTurboSquidModels(): Promise<Model3DApi[]> {
    const turboSquidModels: Model3DApi[] = [
      {
        id: "turbosquid-airplane",
        name: "Boeing 747 Aircraft",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTZmM2ZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJvZWluZyA3NDc8L3RleHQ+PC9zdmc+",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/FlightHelmet/glTF-Binary/FlightHelmet.glb",
        previewUrl: "/model-viewer.html?model=FlightHelmet.glb",
        category: "vehicle",
        tags: ["airplane", "aircraft", "commercial", "aviation"],
        format: "MAX",
        size: "45.2 MB",
        realistic: true,
        source: "TurboSquid",
        license: "Royalty Free",
        description: "Avion commercial Boeing 747 haute définition",
        author: "AeroModels3D",
        rating: 4.9,
        downloads: 12000
      },
      {
        id: "turbosquid-furniture-set",
        name: "Modern Furniture Set",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk1vYmlsaWVyIE1vZGVybmU8L3RleHQ+PC9zdmc+",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/WaterBottle/glTF-Binary/WaterBottle.glb",
        previewUrl: "/model-viewer.html?model=WaterBottle.glb",
        category: "furniture",
        tags: ["furniture", "modern", "interior", "design"],
        format: "OBJ",
        size: "28.7 MB",
        realistic: true,
        source: "TurboSquid",
        license: "Standard",
        description: "Set complet de mobilier moderne pour intérieur",
        author: "InteriorDesign3D",
        rating: 4.4,
        downloads: 6500
      },
      {
        id: "turbosquid-robot",
        name: "Sci-Fi Robot",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWVmZmZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkFuZHJvaWQgU2NpLUZpPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/ReciprocatingSaw/glTF-Binary/ReciprocatingSaw.glb",
        previewUrl: "/model-viewer.html?model=ReciprocatingSaw.glb",
        category: "creature",
        tags: ["robot", "sci-fi", "android", "futuristic"],
        format: "FBX",
        size: "19.4 MB",
        realistic: false,
        source: "TurboSquid",
        license: "Extended",
        description: "Robot androïde futuriste avec détails mécaniques",
        author: "SciFiModels",
        rating: 4.8,
        downloads: 9200
      }
    ];

    return turboSquidModels;
  }

  // Modèles CGTrader simulés
  private async fetchCGTraderModels(): Promise<Model3DApi[]> {
    const cgTraderModels: Model3DApi[] = [
      {
        id: "cgtrader-tree-pack",
        name: "Realistic Tree Pack",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmOGZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkFyYnJlcyBSw6lhbGlzdGVzPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/Avocado/glTF-Binary/Avocado.glb",
        previewUrl: "/model-viewer.html?model=Avocado.glb",
        category: "nature",
        tags: ["trees", "nature", "forest", "vegetation"],
        format: "GLB",
        size: "35.6 MB",
        realistic: true,
        source: "CGTrader",
        license: "Royalty Free",
        description: "Pack de 20 arbres photoréalistes pour environnements",
        author: "NatureModels3D",
        rating: 4.7,
        downloads: 8900
      },
      {
        id: "cgtrader-kitchen",
        name: "Complete Kitchen Set",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmZmOGY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkN1aXNpbmUgQ29tcGzDqHRlPC90ZXh0Pjwvc3ZnPg==",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/Lantern/glTF-Binary/Lantern.glb",
        previewUrl: "/model-viewer.html?model=Lantern.glb",
        category: "furniture",
        tags: ["kitchen", "appliances", "interior", "home"],
        format: "MAX",
        size: "52.1 MB",
        realistic: true,
        source: "CGTrader",
        license: "Standard",
        description: "Cuisine complète avec électroménager et accessoires",
        author: "KitchenDesign Pro",
        rating: 4.6,
        downloads: 5400
      },
      {
        id: "cgtrader-spaceship",
        name: "Futuristic Spaceship",
        thumbnail: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWVmZmZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkZhaXNzZWF1IFNwYXRpYWw8L3RleHQ+PC9zdmc+",
        downloadUrl: "https://github.com/KhronosGroup/glTF-Sample-Assets/raw/main/Models/Corset/glTF-Binary/Corset.glb",
        previewUrl: "/model-viewer.html?model=Corset.glb",
        category: "vehicle",
        tags: ["spaceship", "sci-fi", "space", "futuristic"],
        format: "FBX",
        size: "31.8 MB",
        realistic: false,
        source: "CGTrader",
        license: "Extended",
        description: "Vaisseau spatial futuriste avec intérieur détaillé",
        author: "SpaceModels3D",
        rating: 4.9,
        downloads: 11500
      }
    ];

    return cgTraderModels;
  }

  // Recherche de modèles avec cache
  async searchModels(params: SearchParams = {}): Promise<SearchResult> {
    const cacheKey = JSON.stringify(params);
    const now = Date.now();

    // Vérifier le cache
     if (this.cache.has(cacheKey)) {
       const expiry = this.cacheExpiry.get(cacheKey)!;
       if (now < expiry) {
         return this.cache.get(cacheKey)!;
       }
     }

     try {
      // Récupérer les modèles de toutes les sources
      const [khronosModels, sketchfabModels, mixamoModels, turboSquidModels, cgTraderModels] = await Promise.all([
        this.fetchKhronosModels(),
        this.fetchSketchfabModels(),
        this.fetchMixamoModels(),
        this.fetchTurboSquidModels(),
        this.fetchCGTraderModels()
      ]);

      let allModels = [...khronosModels, ...sketchfabModels, ...mixamoModels, ...turboSquidModels, ...cgTraderModels];

      // Appliquer les filtres
      if (params.query) {
        const query = params.query.toLowerCase();
        allModels = allModels.filter(model => 
          model.name.toLowerCase().includes(query) ||
          model.description.toLowerCase().includes(query) ||
          model.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }

      if (params.category && params.category !== 'all') {
        allModels = allModels.filter(model => model.category === params.category);
      }

      if (params.source && params.source !== 'all') {
        allModels = allModels.filter(model => model.source === params.source);
      }

      if (params.realistic !== undefined) {
        allModels = allModels.filter(model => model.realistic === params.realistic);
      }

      if (params.format && params.format !== 'all') {
        allModels = allModels.filter(model => model.format === params.format);
      }

      if (params.license && params.license !== 'all') {
        allModels = allModels.filter(model => model.license === params.license);
      }

      // Filtrage par tags
      if (params.tags && params.tags.length > 0) {
        allModels = allModels.filter(model => 
          params.tags!.some(tag => model.tags.includes(tag))
        );
      }

      // Filtrage par catégories de tags
      if (params.categories && params.categories.length > 0) {
        allModels = allModels.filter(model => 
          params.categories!.includes(model.category)
        );
      }

      const total = allModels.length;

      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 20;
      const startIndex = (page - 1) * limit;
      const paginatedModels = allModels.slice(startIndex, startIndex + limit);

      const result = {
        models: paginatedModels,
        total
      };

      // Mettre en cache
      this.cache.set(cacheKey, result);
      this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);

      return result;
    } catch (error) {
      console.error('Erreur lors de la recherche de modèles:', error);
      return { models: [], total: 0 };
    }
  }

  // Obtenir un modèle par ID
  async getModelById(id: string): Promise<Model3DApi | null> {
    const result = await this.searchModels();
    return result.models.find(model => model.id === id) || null;
  }

  // Obtenir les catégories disponibles
  async getCategories(): Promise<string[]> {
    const result = await this.searchModels();
    const categories = Array.from(new Set(result.models.map(model => model.category)));
    return categories.sort();
  }

  // Obtenir les sources disponibles
  async getSources(): Promise<string[]> {
    const result = await this.searchModels();
    const sources = Array.from(new Set(result.models.map(model => model.source)));
    return sources.sort();
  }

  // Obtenir les formats disponibles
  async getFormats(): Promise<string[]> {
    const result = await this.searchModels();
    const formats = Array.from(new Set(result.models.map(model => model.format)));
    return formats.sort();
  }

  // Obtenir les licences disponibles
  async getLicenses(): Promise<string[]> {
    const result = await this.searchModels();
    const licenses = Array.from(new Set(result.models.map(model => model.license)));
    return licenses.sort();
  }

  // Télécharger un modèle
  async downloadModel(model: Model3DApi): Promise<boolean> {
    try {
      if (model.source === 'Khronos Group') {
        // Téléchargement direct pour les modèles Khronos
        const response = await fetch(model.downloadUrl);
        if (!response.ok) throw new Error('Échec du téléchargement');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${model.name}.${model.format.toLowerCase()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return true;
      } else {
        // Ouvrir la page source pour les autres
        window.open(model.downloadUrl, '_blank');
        return true;
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      return false;
    }
  }

  // Nettoyer le cache
  clearCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
  }
}

export default ModelApiService;
export type { Model3DApi, SearchParams, SearchResult };